import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { OutageInterval, UptimeDay } from "../../types/videoHistory";
import { fmtTime, formatDuration, uptimeColor } from "../../lib/historyFormat";
import { timeRangeLabel, todayIso, type TimeRange } from "../../lib/timeRange";
import { useIsMobile } from "../../hooks/useMediaQuery";
import Tip from "../Tip";

/**
 * 도움말 툴팁의 구조화 콘텐츠. 평문 한 덩어리 대신 의미 단위로 나눠 포맷을 맞춘다.
 *   summary — 한 줄 요약(무엇을 보여주나)
 *   basis   — 집계 근거(어떤 데이터를 어떻게 계산하나)
 *   rows    — 화면 항목의 의미(용어 → 설명)
 *   legend  — 색상 범례(색 칩 → 의미)
 *   period  — 기간/갱신 근거
 *   note    — 보조 각주
 */
export type InfoContent = {
  summary: string;
  basis?: string;
  rows?: { term: string; desc: string }[];
  legend?: { swatch: string; label: string }[];
  period?: string;
  note?: string;
};

/** 구조화 콘텐츠를 스크린리더용 한 줄 문자열로 평탄화. */
function infoAriaLabel(c: InfoContent): string {
  return [
    c.summary,
    c.basis,
    ...(c.rows?.map((r) => `${r.term}: ${r.desc}`) ?? []),
    ...(c.legend?.map((l) => l.label) ?? []),
    c.period,
    c.note,
  ]
    .filter(Boolean)
    .join(" / ");
}

/** '?' 아이콘 — 마우스오버 / 탭 / 키보드 포커스 시 구조화된 설명 툴팁을 띄운다. */
export function InfoTip({ content }: { content: InfoContent }) {
  return (
    <Tip
      ariaLabel={infoAriaLabel(content)}
      placement="bottom"
      align="start"
      panelClassName="w-80 max-w-[calc(100vw-2rem)] space-y-2 px-3 py-2.5 text-xs font-normal text-slate-100 shadow-xl"
      // 아이콘은 4x4(16px)로 두되, 히트 영역만 사방 14px 넓혀 44px 를 만든다.
      // 아이콘 자체를 키우면 옆 제목과의 균형이 깨진다.
      className="relative inline-flex h-4 w-4 cursor-help select-none items-center justify-center rounded-full border border-slate-300 text-[10px] font-bold leading-none text-slate-400 after:absolute after:-inset-3.5 after:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-slate-600 dark:text-slate-500"
      content={
        <>
          <span className="block font-medium text-slate-50">{content.summary}</span>
          {content.basis && (
            <span className="block text-slate-300">{content.basis}</span>
          )}
          {content.rows && content.rows.length > 0 && (
            <span className="block space-y-1 border-t border-white/10 pt-1.5">
              {content.rows.map((r) => (
                <span key={r.term} className="flex gap-1.5">
                  <span className="shrink-0 font-semibold text-slate-100">
                    {r.term}
                  </span>
                  <span className="text-slate-300">{r.desc}</span>
                </span>
              ))}
            </span>
          )}
          {content.legend && content.legend.length > 0 && (
            <span className="block space-y-1 border-t border-white/10 pt-1.5">
              {content.legend.map((l) => (
                <span key={l.label} className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className={clsx(
                      "inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm",
                      l.swatch,
                    )}
                  />
                  <span className="text-slate-300">{l.label}</span>
                </span>
              ))}
            </span>
          )}
          {content.period && (
            <span className="block border-t border-white/10 pt-1.5 text-slate-400">
              ⏱ {content.period}
            </span>
          )}
          {content.note && (
            <span className="block text-slate-400">{content.note}</span>
          )}
        </>
      }
    >
      ?
    </Tip>
  );
}

/** 통계 섹션 카드 (제목 + 선택 ? 툴팁 + 우측 힌트). */
export function SectionCard({
  title,
  hint,
  info,
  children,
}: {
  title: string;
  hint?: string;
  info?: InfoContent;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-4 shadow-sm sm:p-5 dark:border-surface-darkBorder dark:bg-surface-darkSubtle">
      {/*
        좁은 폭에서는 제목과 힌트를 세로로 쌓는다. 한 줄에 밀어 넣으면 제목이
        "문제 많은 주차 / 장" 처럼 쪼개지고 ? 아이콘이 그 사이에 끼어 읽기 어려워진다.
      */}
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
        <h3 className="flex items-center gap-1.5 text-base font-semibold text-slate-900 dark:text-slate-100">
          <span className="min-w-0">{title}</span>
          {info && <InfoTip content={info} />}
        </h3>
        {hint && (
          <span className="text-xs text-slate-400 dark:text-slate-500">{hint}</span>
        )}
      </div>
      {children}
    </section>
  );
}

export function EmptyHint({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed border-surface-border px-4 py-6 text-center text-sm text-slate-400 dark:border-surface-darkBorder dark:text-slate-500">
      {text}
    </div>
  );
}

/** 주차장 그룹 헤더 — "중앙역 사거리 · PKL...". */
export function LotHeader({ label, sub }: { label: string; sub?: string }) {
  return (
    <div className="mb-2 flex items-baseline gap-2">
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        {label}
      </span>
      {sub && (
        <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
          {sub}
        </span>
      )}
    </div>
  );
}

const UPTIME_BAR_DAYS = 90;
/**
 * 모바일에서 보여줄 일수. 375px 기준 콘텐츠 폭이 약 327px 인데, 90칸을 밀어 넣으면 칸당 2px 라
 * 손가락으로 특정 날짜를 짚는 게 불가능하다. 30칸이면 칸당 약 10px 로 겨우 짚을 만해진다.
 */
const UPTIME_BAR_DAYS_MOBILE = 30;

/**
 * 지금 화면에서 가동률 막대가 실제로 그리는 일수.
 *
 * <p>섹션 헤더의 "최근 N일" 문구도 이 값을 써야 한다 — 막대는 30일인데 라벨만 90일이면
 * 사용자가 없는 데이터를 찾게 된다. 값을 여기 한 곳에 두는 이유가 그것이다.</p>
 */
export function useUptimeBarDays(): number {
  return useIsMobile() ? UPTIME_BAR_DAYS_MOBILE : UPTIME_BAR_DAYS;
}

/** 최근 N일의 ISO date(YYYY-MM-DD) 배열을 오래된→최신 순으로. KST 로컬 기준. */
function lastNDays(n: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${day}`);
  }
  return out;
}

/**
 * 막대 한 칸. hover / 탭 / 포커스 시 그날 상세를 노출.
 *
 * <p>예전에는 {@code tabIndex} 없는 {@code <div>} 에 {@code group-hover:} 만 걸려 있어서
 * 터치 기기에서는 그날 상세를 <strong>볼 방법이 아예 없었다</strong> — 이 팝오버에만 있는
 * 정보(정체 구간 시각)라 모바일에서는 기능이 통째로 빠진 셈이었다. {@link Tip} 으로 바꿔
 * 탭으로도 열리게 한다.</p>
 */
function UptimeSlot({
  kind,
  uptimePct,
  tooltipTitle,
  tooltipBody,
  intervals,
}: {
  kind: "data" | "empty" | "nodata";
  uptimePct?: number;
  tooltipTitle: string;
  tooltipBody: string;
  /** 그날 겹친 정체 구간 — 있으면 '몇시~몇시 몇시간' 목록을 popover 에 덧붙인다. */
  intervals?: OutageInterval[];
}) {
  const colorClass =
    kind === "empty"
      ? "bg-slate-200 dark:bg-slate-700"
      : kind === "nodata"
        ? "bg-slate-400 dark:bg-slate-500"
        : uptimeColor(uptimePct ?? 100);
  const hasIntervals = !!intervals && intervals.length > 0;
  const ariaIntervals = hasIntervals
    ? " — " +
      intervals!
        .map(
          (iv) =>
            `${fmtTime(iv.startedAt)}~${fmtTime(iv.endedAt)} ${
              iv.ongoing ? "진행 중" : formatDuration(iv.durationSec)
            }`,
        )
        .join(", ")
    : "";

  return (
    <Tip
      ariaLabel={`${tooltipTitle} — ${tooltipBody}${ariaIntervals}`}
      placement="top"
      align="center"
      panelClassName="whitespace-nowrap"
      wrapperClassName="flex-1"
      // 슬롯은 좁아서 폭으로는 44px 를 만들 수 없다. 대신 좌우 히트 영역을 살짝 넓히고
      // (막대 사이 간격만큼), 모바일에서는 표시 일수를 줄여 칸 자체를 넓힌다.
      className={clsx(
        "relative h-8 w-full cursor-pointer rounded-sm transition-transform",
        "after:absolute after:-inset-x-0.5 after:inset-y-0 after:content-['']",
        "hover:scale-y-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
        colorClass,
      )}
      content={
        <>
          <span className="block font-semibold">{tooltipTitle}</span>
          <span className="block text-slate-200">{tooltipBody}</span>
          {hasIntervals && (
            <span className="mt-1 block space-y-0.5 border-t border-white/15 pt-1 text-left">
              {intervals!.map((iv, i) => (
                <span
                  key={`${iv.startedAt}-${i}`}
                  className="block tabular-nums text-slate-200"
                >
                  {fmtTime(iv.startedAt)} ~ {fmtTime(iv.endedAt)}
                  {" · "}
                  {iv.ongoing ? (
                    <span className="text-rose-300">진행 중</span>
                  ) : (
                    formatDuration(iv.durationSec)
                  )}
                </span>
              ))}
            </span>
          )}
        </>
      }
    >
      <span className="sr-only">{tooltipTitle}</span>
    </Tip>
  );
}

const NODATA_DOMINANT_SEC = 43200; // 하루의 절반 이상 무신호면 그날을 '연결 끊김(회색)'으로 본다.

/** 영상분석 무신호(연결 끊김)가 지배적인 날인지 — 브릿지 날(noDataSeconds 없음)은 항상 false. */
function isNoDataDay(d: UptimeDay): boolean {
  return (d.noDataSeconds ?? 0) >= NODATA_DOMINANT_SEC;
}

/** 일별 uptime 막대 한 줄 — 클로드 status 페이지 스타일.
 *
 * <p>최근 {@link UPTIME_BAR_DAYS} 일을 항상 슬롯 단위(일별)로 표시한다.
 * 데이터가 있는 날은 가동률에 따라 초록/주황/빨강, 데이터가 없는 날은 회색
 * placeholder("기록 없음"). 호버 시 그날의 상세 정보가 popover 로 뜬다.</p>
 */
export function UptimeRow({
  label,
  days,
}: {
  label: string;
  days: UptimeDay[];
}) {
  // day(YYYY-MM-DD) → UptimeDay 인덱싱 (없는 날은 placeholder)
  const byDay = new Map(days.map((d) => [d.day, d]));
  const isoDays = lastNDays(useUptimeBarDays());
  // 평균/기록은 '정상 관측된 날'만 — 무신호(연결 끊김) 날은 제외한다.
  // 범위를 화면에 그린 구간으로 한정한다: 막대는 30일인데 평균은 90일치면 라벨이 거짓말이 된다.
  const windowStart = isoDays[0];
  const inWindow = days.filter((d) => d.day >= windowStart);
  const filled = inWindow.filter((d) => !isNoDataDay(d));
  const noDataDays = inWindow.filter(isNoDataDay).length;
  const hasData = filled.length > 0;
  const avg = hasData
    ? filled.reduce((s, d) => s + d.uptimePct, 0) / filled.length
    : null;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {label}
        </span>
        <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
          {avg != null
            ? `평균 ${avg.toFixed(2)}% · 기록 ${filled.length}일`
            : "기록 없음"}
          {noDataDays > 0 ? ` · 끊김 ${noDataDays}일` : ""}
        </span>
      </div>
      {/* 슬롯이 flex-1 이라 남는 폭을 나눠 갖는다 — 일수가 줄면 칸이 그만큼 넓어진다. */}
      <div className="flex h-8 items-end gap-0.5">
        {isoDays.map((iso) => {
          const d = byDay.get(iso) ?? null;
          if (d == null) {
            return (
              <UptimeSlot
                key={iso}
                kind="empty"
                tooltipTitle={`${iso} · 기록 없음`}
                tooltipBody="이 날은 일별 요약이 적재되지 않았습니다 (lot 등록 전 또는 매일 자정 집계 이전)."
              />
            );
          }
          if (isNoDataDay(d)) {
            return (
              <UptimeSlot
                key={d.day}
                kind="nodata"
                tooltipTitle={`${d.day} · 연결 끊김`}
                tooltipBody={`현장 GPU 무신호 ${formatDuration(d.noDataSeconds ?? 0)}`}
              />
            );
          }
          return (
            <UptimeSlot
              key={d.day}
              kind="data"
              uptimePct={d.uptimePct}
              tooltipTitle={`${d.day}`}
              tooltipBody={`가동률 ${d.uptimePct.toFixed(2)}% · 문제 ${formatDuration(d.problemSeconds)}`}
              intervals={d.intervals}
            />
          );
        })}
      </div>
    </div>
  );
}

/** 기간 선택 — 3 프리셋(7/30/90일) + 커스텀(1~365). */
export const PERIOD_OPTIONS: { days: number; label: string }[] = [
  { days: 7, label: "7일" },
  { days: 30, label: "30일" },
  { days: 90, label: "90일" },
];

const CUSTOM_DEFAULT_DAYS = 14;
const CUSTOM_MIN_DAYS = 1;
const CUSTOM_MAX_DAYS = 365;

export function PeriodSelector({
  value,
  onChange,
}: {
  value: number;
  onChange: (days: number) => void;
}) {
  const isPreset = PERIOD_OPTIONS.some((opt) => opt.days === value);
  return (
    <div className="inline-flex items-center gap-2">
      <div className="inline-flex rounded-md border border-surface-border dark:border-surface-darkBorder">
        {PERIOD_OPTIONS.map((opt, i) => (
          <button
            key={opt.days}
            type="button"
            onClick={() => onChange(opt.days)}
            className={clsx(
              "min-h-[2.75rem] px-3 py-2 text-xs font-medium transition-colors sm:min-h-0 sm:px-2.5 sm:py-1",
              i > 0 &&
                "border-l border-surface-border dark:border-surface-darkBorder",
              value === opt.days
                ? "bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
            )}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(isPreset ? CUSTOM_DEFAULT_DAYS : value)}
          className={clsx(
            "min-h-[2.75rem] border-l border-surface-border px-3 py-2 text-xs font-medium transition-colors sm:min-h-0 sm:px-2.5 sm:py-1 dark:border-surface-darkBorder",
            !isPreset
              ? "bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900"
              : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
          )}
          aria-label="커스텀 기간 선택"
        >
          커스텀
        </button>
      </div>
      {!isPreset && (
        <label className="inline-flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="number"
            min={CUSTOM_MIN_DAYS}
            max={CUSTOM_MAX_DAYS}
            value={value}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (Number.isNaN(n)) return;
              const clamped = Math.max(
                CUSTOM_MIN_DAYS,
                Math.min(CUSTOM_MAX_DAYS, n),
              );
              onChange(clamped);
            }}
            className="w-14 rounded border border-surface-border bg-white px-1.5 py-0.5 text-right text-xs tabular-nums dark:border-surface-darkBorder dark:bg-slate-900"
            aria-label={`기간 일수 (${CUSTOM_MIN_DAYS}~${CUSTOM_MAX_DAYS})`}
          />
          일
        </label>
      )}
    </div>
  );
}

/**
 * Time range picker — Grafana/Datadog/Sentry 패턴.
 *
 * <p>트리거 버튼 한 개로 평소엔 작은 공간만 차지하고, 클릭 시 split panel 이 열려
 * 좌측은 quick presets(상대 기간), 우측은 사용자 지정(from~to 날짜) 을 노출한다.
 * 외부 클릭/ESC 로 닫힘.</p>
 *
 * <p>value 는 {@link TimeRange} — relative(최근 N일) 또는 absolute(from~to).
 * 백엔드 top-problem-* 엔드포인트가 양쪽 모두 수신 (?days= / ?from=&to=).</p>
 */
const TIME_RANGE_PRESETS: { label: string; days: number }[] = [
  { label: "최근 1일", days: 1 },
  { label: "최근 7일", days: 7 },
  { label: "최근 30일", days: 30 },
  { label: "최근 90일", days: 90 },
  { label: "최근 180일", days: 180 },
  { label: "최근 365일", days: 365 },
];

export function TimeRangePicker({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // 사용자 지정(절대 구간) draft — '적용' 전까지는 onChange 하지 않는다.
  const [draftFrom, setDraftFrom] = useState(
    value.kind === "absolute" ? value.from : "",
  );
  const [draftTo, setDraftTo] = useState(
    value.kind === "absolute" ? value.to : todayIso(),
  );

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const triggerLabel = timeRangeLabel(value);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={clsx(
          "inline-flex min-h-[2.75rem] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2 text-xs font-medium transition-colors sm:min-h-0 sm:py-1",
          "border-surface-border bg-white text-slate-700 hover:bg-slate-50",
          "dark:border-surface-darkBorder dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.4.22.53l3 3a.75.75 0 101.06-1.06l-2.78-2.78V5z"
            clipRule="evenodd"
          />
        </svg>
        <span>{triggerLabel}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={clsx(
            "h-3 w-3 text-slate-400 transition-transform dark:text-slate-500",
            open && "rotate-180",
          )}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="기간 선택"
          className={clsx(
            "absolute left-0 z-30 mt-1.5 flex w-[22rem] overflow-hidden rounded-lg border bg-white shadow-lg",
            "border-surface-border dark:border-surface-darkBorder dark:bg-slate-900",
          )}
        >
          <ul className="flex w-1/2 flex-col border-r border-surface-border py-1.5 dark:border-surface-darkBorder">
            <li className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              빠른 선택
            </li>
            {TIME_RANGE_PRESETS.map((p) => {
              const active = value.kind === "relative" && value.days === p.days;
              return (
                <li key={p.days}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange({ kind: "relative", days: p.days });
                      setOpen(false);
                    }}
                    className={clsx(
                      "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs font-medium transition-colors",
                      active
                        ? "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100"
                        : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/60",
                    )}
                  >
                    <span>{p.label}</span>
                    {active && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-3.5 w-3.5"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4L8.5 12 15.3 5.3a1 1 0 011.4 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="flex w-1/2 flex-col gap-2 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              사용자 지정
            </div>
            <label className="flex flex-col gap-1 text-[11px] text-slate-500 dark:text-slate-400">
              시작일
              <input
                type="date"
                value={draftFrom}
                max={draftTo || todayIso()}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="rounded border border-surface-border bg-white px-1.5 py-1 text-xs tabular-nums dark:border-surface-darkBorder dark:bg-slate-800 dark:text-slate-200 dark:[color-scheme:dark]"
                aria-label="시작일"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-slate-500 dark:text-slate-400">
              종료일
              <input
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                max={todayIso()}
                onChange={(e) => setDraftTo(e.target.value)}
                className="rounded border border-surface-border bg-white px-1.5 py-1 text-xs tabular-nums dark:border-surface-darkBorder dark:bg-slate-800 dark:text-slate-200 dark:[color-scheme:dark]"
                aria-label="종료일"
              />
            </label>
            <button
              type="button"
              disabled={!draftFrom || !draftTo}
              onClick={() => {
                // from > to 입력 시 swap (BE 도 swap 하지만 라벨 정합 위해 FE 에서도)
                const [f, t] =
                  draftFrom <= draftTo ? [draftFrom, draftTo] : [draftTo, draftFrom];
                onChange({ kind: "absolute", from: f, to: t });
                setOpen(false);
              }}
              className={clsx(
                "mt-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors",
                draftFrom && draftTo
                  ? "bg-slate-800 text-white hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                  : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600",
              )}
            >
              적용
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 일반화된 토글(segmented control). 상태/사이트 등 enum 같은 필터에 재사용.
 * 한 옵션이 active 면 진하게, 나머지는 hover 만.
 */
export function SegToggle<T extends string | number>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel?: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      // shrink-0 — 여러 토글이 한 줄 가로 스크롤 안에 놓일 때 눌려 찌그러지지 않도록.
      className="inline-flex shrink-0 rounded-md border border-surface-border dark:border-surface-darkBorder"
    >
      {options.map((opt, i) => (
        <button
          key={String(opt.value)}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          className={clsx(
            // 모바일 44px. whitespace-nowrap 은 "안산워치마 / 일" 처럼 버튼 안에서 글자가
            // 두 줄로 쪼개지던 문제를 막는다 — 대신 줄 전체가 가로 스크롤된다.
            "min-h-[2.75rem] whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors",
            "sm:min-h-0 sm:px-2.5 sm:py-1",
            i > 0 && "border-l border-surface-border dark:border-surface-darkBorder",
            value === opt.value
              ? "bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900"
              : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * 필터 컨트롤 한 줄.
 *
 * <p>모바일에서는 토글 4개가 세로로 쌓이면 필터만 200px 을 먹어 정작 봐야 할 목록이
 * 화면 밖으로 밀린다. 그래서 좁은 폭에서는 <strong>한 줄 가로 스크롤</strong>로 두고,
 * 카드 좌우 여백까지 흘려보내(-mx-4) 잘린 토글이 보이게 해 스크롤 가능함을 알린다.
 * sm 이상에서는 기존처럼 줄바꿈 배치.</p>
 */
export function FilterRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex gap-2 overflow-x-auto pb-1 max-sm:-mx-4 max-sm:px-4 sm:flex-wrap sm:items-center sm:overflow-visible sm:pb-0">
      {children}
    </div>
  );
}

/** 검색 인풋 — 주차장명/코드 필터용. 우측 X 로 즉시 초기화. */
export function SearchInput({
  value,
  onChange,
  placeholder = "주차장 검색",
  fullWidth = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /**
   * 모바일에서 한 줄을 통째로 쓸지. 검색창만 단독으로 놓인 섹션에서만 켠다 —
   * {@link FilterRow} 안(다른 컨트롤과 같은 줄)에서 켜면 옆 컨트롤을 밀어내 글자가 깨진다.
   */
  fullWidth?: boolean;
}) {
  return (
    <div
      className={clsx(
        "relative inline-flex items-center",
        fullWidth ? "w-full sm:w-auto" : "shrink-0",
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-slate-400 dark:text-slate-500"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.45 4.39l3.08 3.08a1 1 0 01-1.42 1.42l-3.08-3.08A7 7 0 012 9z"
          clipRule="evenodd"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        // 모바일은 폭 full + 높이 44px — 검색은 한 손 입력이 잦아 넉넉하게 둔다.
        // text-base 는 iOS Safari 가 16px 미만 입력에서 자동 확대하는 걸 막는다.
        className={clsx(
          "min-h-[2.75rem] rounded-md border border-surface-border bg-white py-1 pl-7 pr-6 text-slate-700 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:min-h-0 dark:border-surface-darkBorder dark:bg-slate-900 dark:text-slate-200",
          // text-base 는 iOS Safari 가 16px 미만 입력에서 자동 확대하는 걸 막는다.
          "text-base sm:text-xs",
          fullWidth ? "w-full sm:w-48" : "w-44 sm:w-48",
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="검색어 지우기"
          className="absolute right-1.5 flex h-4 w-4 items-center justify-center rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        >
          ×
        </button>
      )}
    </div>
  );
}

/** 현재 페이지 주변 + 첫/끝 페이지를 보이는 창(window). -1 은 생략(…) 표식. */
function pageWindow(current: number, pageCount: number): number[] {
  const set = new Set<number>([0, pageCount - 1]);
  for (let p = current - 1; p <= current + 1; p++) {
    if (p >= 0 && p < pageCount) set.add(p);
  }
  const sorted = [...set].filter((p) => p >= 0).sort((a, b) => a - b);
  const out: number[] = [];
  let prev = -2;
  for (const p of sorted) {
    if (p - prev > 1) out.push(-1);
    out.push(p);
    prev = p;
  }
  return out;
}

function PagerArrow({
  disabled,
  onClick,
  label,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className="inline-flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded border border-surface-border px-2 py-1 tabular-nums disabled:opacity-40 enabled:hover:bg-slate-50 sm:min-h-0 sm:min-w-0 dark:border-surface-darkBorder dark:enabled:hover:bg-slate-800/40"
    >
      {children}
    </button>
  );
}

/** 숫자 기반 페이지네이션 — 첫/끝 + 현재 주변 페이지 번호 + 이전/다음. "X-Y / 총 N". */
export function Pager({
  page,
  size,
  total,
  hasNext,
  onPage,
}: {
  page: number;
  size: number;
  total: number;
  hasNext: boolean;
  onPage: (page: number) => void;
}) {
  if (total === 0) return null;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const from = page * size + 1;
  const to = Math.min((page + 1) * size, total);
  const atEnd = page >= pageCount - 1 || !hasNext;
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
      <span className="tabular-nums">
        {from}–{to} / 총 {total}
      </span>
      <div className="flex items-center gap-1">
        <PagerArrow disabled={page === 0} onClick={() => onPage(page - 1)} label="이전">
          ‹
        </PagerArrow>
        {pageWindow(page, pageCount).map((p, i) =>
          p === -1 ? (
            <span key={`gap-${i}`} className="px-1 text-slate-400 dark:text-slate-500">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPage(p)}
              aria-current={p === page ? "page" : undefined}
              className={clsx(
                "inline-flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded border px-2 py-1 tabular-nums transition-colors",
                "sm:min-h-0 sm:min-w-[1.75rem]",
                p === page
                  ? "border-slate-800 bg-slate-800 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900"
                  : "border-surface-border hover:bg-slate-50 dark:border-surface-darkBorder dark:hover:bg-slate-800/40",
              )}
            >
              {p + 1}
            </button>
          ),
        )}
        <PagerArrow disabled={atEnd} onClick={() => onPage(page + 1)} label="다음">
          ›
        </PagerArrow>
      </div>
    </div>
  );
}

/** 배열을 키별 그룹으로 (삽입 순서 유지). */
export function groupBy<T>(items: T[], key: (t: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const it of items) {
    const k = key(it);
    const arr = map.get(k);
    if (arr) arr.push(it);
    else map.set(k, [it]);
  }
  return map;
}
